import React, { useCallback, useMemo, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import type { DurationInput } from '@fullcalendar/core';
import { useTranslation } from '@modernloop/i18n';
import { Dialog, FCWithFragments, NestedMenuItem } from '@modernloop/shared/components';
import { formatDurationFromMinutes, getDateFnsLocale } from '@modernloop/shared/datetime';
import { CanDisable } from '@modernloop/shared/helper-components';
import {
  ArrowDownIcon,
  BeverageCoffeeIcon,
  ClockWithArchedArrowIcon,
  MoonWithArchedArrowIcon,
  MoonWithStarsIcon,
  TriangleDownIcon,
} from '@modernloop/shared/icons';
import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { formatDuration } from 'date-fns';

import { Break_BreakSlotFragment, Break_SchedulingWindowFragment, InterviewType } from 'src/generated/mloop-graphql';

import { MINUTES_IN_A_DAY, SECONDS_IN_A_DAY } from 'src/constants';

type Fragments = {
  breakSlot: Break_BreakSlotFragment;
  schedulingWindow: Break_SchedulingWindowFragment | undefined;
};

type Props = {
  readonly?: boolean;
  minSchedulingWindowSecondsNeeded: number;
  onUpdated: (breakSlot: Break_BreakSlotFragment, schedulingWindowDuration?: DurationInput) => void;
};

export const DURATION_OPTIONS: { id: number; value: string }[] = [
  { id: 5, value: '5 mins' },
  { id: 10, value: '10 mins' },
  { id: 15, value: '15 mins' },
  { id: 20, value: '20 mins' },
  { id: 25, value: '25 mins' },
  { id: 30, value: '30 mins' },
  { id: 35, value: '35 mins' },
  { id: 40, value: '40 mins' },
  { id: 45, value: '45 mins' },
  { id: 50, value: '50 mins' },
  { id: 55, value: '55 mins' },
  { id: 60, value: '1 hr' },
  { id: 75, value: '1 hr 15 mins' },
  { id: 80, value: '1 hr 20 mins' },
  { id: 90, value: '1 hr 30 mins' },
  { id: 105, value: '1 hr 45 mins' },
  { id: 115, value: '1 hr 55 mins' }, // This is backfill for old data
  { id: 120, value: '2 hrs' },
  { id: 135, value: '2 hrs 15 mins' },
  { id: 150, value: '2 hrs 30 mins' },
  { id: 165, value: '2 hrs 45 mins' },
  { id: 180, value: '3 hrs' },
  { id: 195, value: '3 hrs 15 mins' },
  { id: 210, value: '3 hrs 30 mins' },
  { id: 225, value: '3 hrs 45 mins' },
  { id: 240, value: '4 hrs' },
  { id: 300, value: '5 hrs' },
  { id: 360, value: '6 hrs' },
  { id: 420, value: '7 hrs' },
  { id: 480, value: '8 hrs' },
  { id: 540, value: '9 hrs' },
  { id: 600, value: '10 hrs' },
  { id: 660, value: '11 hrs' },
  { id: 720, value: '12 hrs' },
  // { id: MINUTES_IN_A_DAY, value: '1 Day' },
];

const BreakSlot: FCWithFragments<Fragments, Props> = ({
  readonly,
  minSchedulingWindowSecondsNeeded,
  breakSlot,
  schedulingWindow,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showScheduleWindowIncrementWindow, setShowScheduleWindowIncrementWindow] = useState(false);

  const [tempBreakType, setTempBreakType] = useState<InterviewType | null>(null);
  const [tempDuration, setTempDuration] = useState<number | null>(null);

  const label = useMemo(() => {
    switch (breakSlot.interviewType) {
      case InterviewType.AnyTimeLater: {
        return t('hiring-dashboard-interviewers:interviewers.plan.break.thenAnyTimeLater');
      }
      case InterviewType.Break: {
        if (breakSlot.duration === 0) {
          return t('hiring-dashboard-interviewers:interviewers.plan.break.thenImmediatelyAfter');
        }
        const duration = formatDurationFromMinutes(breakSlot.duration ?? 0, { abbreviate: true });
        return t('hiring-dashboard-interviewers:interviewers.plan.break.thenDurationLater', { duration });
      }
      case InterviewType.NextDay: {
        return t('hiring-dashboard-interviewers:interviewers.plan.break.thenNextDay');
      }
      case InterviewType.DayDivider: {
        return t('hiring-dashboard-interviewers:interviewers.plan.break.thenNextDayOrLater');
      }
      default:
        return '';
    }
  }, [breakSlot.duration, breakSlot.interviewType, t]);

  const icon = useMemo(() => {
    switch (breakSlot.interviewType) {
      case InterviewType.AnyTimeLater: {
        return <ClockWithArchedArrowIcon data-testid="break-clock-with-arched-arrow-icon" />;
      }
      case InterviewType.Break: {
        if (breakSlot.duration === 0) {
          return <ArrowDownIcon data-testid="break-arrow-down-icon" />;
        }
        return <BeverageCoffeeIcon data-testid="break-beverage-coffee-icon" />;
      }
      case InterviewType.NextDay: {
        return <MoonWithStarsIcon data-testid="break-moon-with-stars-icon" />;
      }
      case InterviewType.DayDivider: {
        return <MoonWithArchedArrowIcon data-testid="break-moon-with-arched-arrow-icon" />;
      }
      default:
        return null;
    }
  }, [breakSlot.duration, breakSlot.interviewType]);

  const handleSelect = useCallback(
    ({
      interviewType,
      duration,
      skipMinSchedulingWindowCheck,
      schedulingWindowDuration,
    }: {
      interviewType: InterviewType;
      duration?: number;
      skipMinSchedulingWindowCheck?: boolean;
      schedulingWindowDuration?: DurationInput;
    }) => {
      if (interviewType === breakSlot.interviewType && duration === breakSlot.duration) {
        setShowMenu(false);
        return;
      }

      if (
        !skipMinSchedulingWindowCheck &&
        (interviewType === InterviewType.DayDivider ||
          (interviewType === InterviewType.Break && duration === MINUTES_IN_A_DAY)) &&
        schedulingWindow?.seconds &&
        minSchedulingWindowSecondsNeeded >= schedulingWindow.seconds // Adding a day break will increase the scheduling window
      ) {
        setTempBreakType(interviewType);
        setTempDuration(duration || null);
        setShowScheduleWindowIncrementWindow(true);
        setShowMenu(false);
        return;
      }

      if (duration === MINUTES_IN_A_DAY) {
        onUpdated({ ...breakSlot, interviewType: InterviewType.NextDay, duration: 0 }, schedulingWindowDuration);
      } else {
        onUpdated({ ...breakSlot, interviewType, duration }, schedulingWindowDuration);
      }

      setShowMenu(false);
    },
    [breakSlot, minSchedulingWindowSecondsNeeded, onUpdated, schedulingWindow]
  );

  const handleSchedulingWindowChange = useCallback(() => {
    if (!tempBreakType) {
      return;
    }

    handleSelect({
      interviewType: tempBreakType,
      duration: tempDuration || undefined,
      skipMinSchedulingWindowCheck: true,
      schedulingWindowDuration: { seconds: minSchedulingWindowSecondsNeeded + SECONDS_IN_A_DAY },
    });

    setShowScheduleWindowIncrementWindow(false);
    setTempBreakType(null);
  }, [handleSelect, minSchedulingWindowSecondsNeeded, tempBreakType, tempDuration]);

  return (
    <CanDisable disabled={Boolean(readonly)}>
      <Stack direction="column" alignItems="center" justifyContent="center">
        <Button
          ref={buttonRef}
          startIcon={icon}
          endIcon={<TriangleDownIcon />}
          data-testid="break-label-button"
          onClick={() => setShowMenu(true)}
        >
          {label}
        </Button>
      </Stack>
      <Menu
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={showMenu}
        data-testid="break-duration-picker-menu"
        onClose={() => setShowMenu(false)}
      >
        <MenuItem
          value={InterviewType.AnyTimeLater}
          data-testid="break-any-time-later-menu-option"
          onClick={() => handleSelect({ interviewType: InterviewType.AnyTimeLater })}
        >
          {t('hiring-dashboard-interviewers:interviewers.plan.break.anyTimeLater')}
        </MenuItem>
        <MenuItem
          value={`${InterviewType.Break}-0`}
          data-testid="break-immediately-after-menu-option"
          onClick={() => handleSelect({ interviewType: InterviewType.Break, duration: 0 })}
        >
          {t('hiring-dashboard-interviewers:interviewers.plan.break.immediatelyAfter')}
        </MenuItem>
        <NestedMenuItem
          title={t('hiring-dashboard-interviewers:interviewers.plan.break.specificDurationAfter')}
          sx={{ height: '480px' }}
          dataTestId="break-specific-duration-picker-menu"
          onClose={() => setShowMenu(false)}
        >
          {DURATION_OPTIONS.map((option) => {
            return (
              <MenuItem
                key={option.id}
                value={option.id}
                data-testid={`break-specific-duration-picker-menu-${option.id}`}
                onClick={() => handleSelect({ interviewType: InterviewType.Break, duration: option.id })}
              >
                {formatDurationFromMinutes(option.id, { abbreviate: true })}
              </MenuItem>
            );
          })}
        </NestedMenuItem>
        <MenuItem
          value={InterviewType.DayDivider}
          data-testid="break-next-day-or-later-menu-option"
          onClick={() => handleSelect({ interviewType: InterviewType.DayDivider })}
        >
          {t('hiring-dashboard-interviewers:interviewers.plan.break.nextDayOrLater')}
        </MenuItem>
      </Menu>

      {showScheduleWindowIncrementWindow && (
        <Dialog
          dataTestId="break-scheduling-window-increment-dialog"
          open
          title={t('hiring-dashboard-interviewers:interviewers.plan.break.increaseSchedulingWindow')}
          onClose={() => setShowScheduleWindowIncrementWindow(false)}
          cancelOptions={{
            label: t('hiring-dashboard-interviewers:interviewers.plan.break.cancel'),
            onClick: () => setShowScheduleWindowIncrementWindow(false),
          }}
          submitOptions={{
            label: t('hiring-dashboard-interviewers:interviewers.plan.break.saveAndUpdate'),
            dataTestId: 'break-scheduling-window-increment-accept-button',
            onClick: handleSchedulingWindowChange,
          }}
        >
          <Typography>
            {t('hiring-dashboard-interviewers:interviewers.plan.break.increaseSchedulingWindowDescription', {
              fromDuration: formatDuration(
                { days: schedulingWindow?.seconds / SECONDS_IN_A_DAY },
                { locale: getDateFnsLocale() }
              ),
              toDuration: formatDuration(
                {
                  days: (minSchedulingWindowSecondsNeeded + SECONDS_IN_A_DAY) / SECONDS_IN_A_DAY,
                },
                { locale: getDateFnsLocale() }
              ),
            })}
          </Typography>
        </Dialog>
      )}
    </CanDisable>
  );
};

BreakSlot.fragments = {
  breakSlot: gql`
    fragment Break_breakSlot on JobStageInterview {
      id
      interviewType
      name
      duration
      isLockedOrder
    }
  `,
  schedulingWindow: gql`
    fragment Break_schedulingWindow on Duration {
      seconds
    }
  `,
};

export default BreakSlot;
